import React, { useState, useEffect } from 'react';
import { Music, ChevronRight, ChevronLeft, Loader2, Play } from 'lucide-react';
import { fetchPacks, fetchPackLoops, cacheAudioUrl } from '@/api/loops';
import { INSTRUMENT_COLORS } from '@/utils/constants';

export const PackBrowser = ({ onAddTrack, onPreviewLoop, onStopPreview, previewingLoopId }) => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [packLoops, setPackLoops] = useState([]);
  const [loadingLoops, setLoadingLoops] = useState(false);

  useEffect(() => {
    fetchPacks()
      .then(setPacks)
      .catch((e) => console.warn('Failed to fetch packs:', e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectPack = async (pack) => {
    setSelectedPack(pack);
    setLoadingLoops(true);
    setPackLoops([]);
    try {
      const loops = await fetchPackLoops(pack.id);
      const validated = (loops || []).map((loop) => ({
        id: loop.id || 'unknown',
        name: loop.name || 'Untitled',
        bpm: loop.bpm || 90,
        key: loop.key || '-',
        instrument_group: loop.instrument_group || 'melody',
        bars: Number(loop.bars) > 0 ? Number(loop.bars) : null,
        energy: loop.energy || 3,
        moods: loop.moods || [],
        pack_code: loop.pack_code || null,
        audio_url: loop.audio_url || '',
      }));
      validated.forEach((lp) => cacheAudioUrl(lp.id, lp.audio_url));
      setPackLoops(validated);
    } catch (e) {
      console.warn('Failed to fetch pack loops:', e.message);
    } finally {
      setLoadingLoops(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-dim)' }} />
      </div>
    );
  }

  // Pack detail view — shows loops in this pack
  if (selectedPack) {
    return (
      <div data-testid="pack-detail" className="flex flex-col">
        <button
          data-testid="pack-back-btn"
          onClick={() => { setSelectedPack(null); setPackLoops([]); }}
          className="flex items-center gap-1 px-3 py-2"
          style={{ fontSize: 11, fontWeight: 600, color: 'var(--green)', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <ChevronLeft size={13} /> All Packs
        </button>
        <div className="px-3 py-1 mb-2">
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{selectedPack.name}</span>
          <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 8 }}>{packLoops.length} loops</span>
        </div>

        {loadingLoops && (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={16} className="animate-spin" style={{ color: 'var(--text-dim)' }} />
          </div>
        )}

        {!loadingLoops && packLoops.length === 0 && (
          <div className="py-4 text-center" style={{ fontSize: 12, color: 'var(--text-dim)' }}>No loops in this pack</div>
        )}

        {!loadingLoops && packLoops.length > 0 && (
          <div className="flex flex-col gap-1.5 px-3 pb-3">
            {packLoops.map((loop) => {
              const color = INSTRUMENT_COLORS[loop.instrument_group] || '#666';
              const playing = previewingLoopId === loop.id;
              return (
                <div
                  key={loop.id}
                  data-testid={`pack-loop-${loop.id}`}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer group"
                  style={{ background: 'var(--bg-card)', borderRadius: 8, border: `1px solid ${playing ? color : 'var(--border)'}` }}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); playing ? onStopPreview?.() : onPreviewLoop?.(loop); }}
                    className="shrink-0 flex items-center justify-center"
                    style={{ width: 26, height: 26, borderRadius: '50%', background: playing ? color : `${color}22`, color: playing ? '#fff' : color, border: 'none', cursor: 'pointer' }}
                  >
                    <Play size={11} fill="currentColor" />
                  </button>
                  <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                    <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{loop.name}</span>
                    <div className="flex items-center gap-2">
                      <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{loop.bpm} BPM</span>
                      <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{loop.key}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{loop.bars ? `${loop.bars}bar` : 'Full loop'}</span>
                    </div>
                  </div>
                  <span className="shrink-0" style={{ fontSize: 9, fontWeight: 600, color: 'var(--bg)', background: color, padding: '1px 6px', borderRadius: 22 }}>{loop.instrument_group}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); onAddTrack?.(loop); }}
                    className="shrink-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--green)', color: 'var(--bg)', border: 'none', cursor: 'pointer' }}
                  >+</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Pack list view
  return (
    <div data-testid="pack-browser" className="flex flex-col gap-1.5 px-3 py-2">
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Themed Packs</span>
      {packs.map((pack) => (
        <button
          key={pack.id}
          data-testid={`pack-${pack.id}`}
          onClick={() => handleSelectPack(pack)}
          className="flex items-center gap-2 px-2.5 py-2 transition-colors w-full text-left"
          style={{ background: 'var(--bg-elevated)', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }}
        >
          <div className="shrink-0 flex items-center justify-center rounded" style={{ width: 36, height: 36, background: 'var(--bg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
            {pack.artwork_url ? (
              <img src={pack.artwork_url} alt={pack.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Music size={14} style={{ color: 'var(--green)' }} />
            )}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{pack.name}</span>
            <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{pack.loop_count || ''} loops</span>
          </div>
          <ChevronRight size={14} style={{ color: 'var(--text-dim)' }} />
        </button>
      ))}
    </div>
  );
};

export default PackBrowser;
